// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/DiceAce12/backend';


// Helper function to handle API requests
const apiRequest = async (endpoint, method = 'GET', data = null, token = null) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = {
        'Content-Type': 'application/json',
    };
    
    // ALWAYS try to get token if not provided
    const authToken = token || TokenService.getToken();
    
    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        // console.log(`API Request to ${endpoint}: Authorization header set with token length ${authToken.length}`);
    } else {
        // console.warn(`API Request to ${endpoint}: No token available`);
    }
    
    const config = {
        method,
        headers,
        credentials: 'include',  // Important for CORS with cookies
    };
    
    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
        config.body = JSON.stringify(data);
    }
    
    try {
        // console.log(`Making ${method} request to: ${url}`);
        // console.log('Headers:', headers);
        
        const response = await fetch(url, config);
        
        // console.log(`Response status for ${endpoint}: ${response.status}`);
        
        // Parse JSON response
        let responseData;
        try {
            responseData = await response.json();
        } catch {
            responseData = {};
        }
        
        // If 401 Unauthorized, clear token and redirect
        if (response.status === 401) {
            // console.error(`401 Unauthorized for ${endpoint}. Clearing tokens.`);
            TokenService.clear();
            
            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
                setTimeout(() => {
                    window.location.href = '/login';
                }, 1000);
            }
        }
        
        // Include HTTP status in the returned object
        return {
            status: response.status,
            ok: response.ok,
            ...responseData,
        };
        
    } catch (error) {
        // console.error('API Request Error:', error);
        // console.error('Error details:', {
        //     endpoint,
        //     method,
        //     hasToken: !!authToken
        // });
        
        return {
            status: 500,
            ok: false,
            error: error.message || 'Network error',
        };
    }
};
  


// Token management
export const TokenService = {
    getToken: () => {
        // Check both possible storage names
        return localStorage.getItem('auth_token') || localStorage.getItem('token');
    },
    
    setToken: (token) => {
        // Store in BOTH places for compatibility
        localStorage.setItem('auth_token', token);
        localStorage.setItem('token', token);
    },
    
    removeToken: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
    },
    
    getRefreshToken: () => {
        return localStorage.getItem('refresh_token');
    },
    
    setRefreshToken: (token) => {
        localStorage.setItem('refresh_token', token);
    },
    
    removeRefreshToken: () => {
        localStorage.removeItem('refresh_token');
    },
    
    clear: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
    }
};

// User Authentication API
export const AuthAPI = {
    // Register new user
    register: async (userData) => {
        return await apiRequest('/api/auth/register', 'POST', userData);
    },
    
    // User login
login: async (username, password) => {
    const response = await apiRequest('/api/auth/login', 'POST', {
        username,
        password
    });
    
    // console.log('Login response:', response);  // Add logging
    
    if (response.token) {
        // Use TokenService consistently
        TokenService.setToken(response.token);
        
        if (response.refresh_token) {
            TokenService.setRefreshToken(response.refresh_token);
        }
        
        if (response.user) {
            localStorage.setItem('user', JSON.stringify(response.user));
        }
        
        // Debug: verify token storage
        // console.log('Token stored successfully:', {
        //     viaTokenService: TokenService.getToken(),
        //     direct: localStorage.getItem('token'),
        //     auth_token: localStorage.getItem('auth_token')
        // });
    }
    
    return response;
},
    
    // Verify email
    verifyEmail: async (token) => {
        return await apiRequest(`/api/auth/verify-email?token=${token}`, 'GET');
    },
    
    // Forgot password
    forgotPassword: async (email) => {
        return await apiRequest('/api/auth/forgot-password', 'POST', { email });
    },
    
    // Reset password
    resetPassword: async (token, newPassword, confirmPassword) => {
        return await apiRequest('/api/auth/reset-password', 'POST', {
            token,
            new_password: newPassword,
            confirm_password: confirmPassword
        });
    },
    
    // Logout
    logout: () => {
        TokenService.clear();
        window.location.href = '/login';
    },
    
    // Get current user from localStorage
    getCurrentUser: () => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            return JSON.parse(userStr);
        }
        return null;
    },
    
    // Check if user is authenticated
    isAuthenticated: () => {
        return !!TokenService.getToken();
    }
};

// User Profile API
export const UserAPI = {
    // Get user profile
    getProfile: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/user/profile', 'GET', null, token);
    },
    
    // Update user profile
    updateProfile: async (profileData) => {
        const token = TokenService.getToken();
        return await apiRequest('/api/user/profile', 'PUT', profileData, token);
    },
    
    // Change password
    changePassword: async (currentPassword, newPassword) => {
        const token = TokenService.getToken();
        return await apiRequest('/api/user/change-password', 'POST', {
            current_password: currentPassword,
            new_password: newPassword
        }, token);
    },
    
    // Resend verification email
    resendVerification: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/user/resend-verification', 'POST', null, token);
    }
};

// Wallet API
export const WalletAPI = {
    // Get wallet balance
    getBalance: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/balance', 'GET', null, token);
    },
    
    // Get deposit address
    getDepositAddress: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/deposit-address', 'GET', null, token);
    },

    getMyWalletAddress: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/my-address', 'GET', null, token);
    },
    
    // Generate new deposit address
    generateDepositAddress: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/generate-address', 'POST', null, token);
    },
    
    // Get transaction history
    getTransactions: async (params = {}) => {
        const token = TokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/wallet/transactions${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Request withdrawal
    requestWithdrawal: async (amount, bitcoinAddress) => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/withdraw', 'POST', {
            amount: parseFloat(amount),
            bitcoin_address: bitcoinAddress
        }, token);
    },
    
    // Check deposit status
    checkDepositStatus: async (txHash) => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/wallet/check-deposit/${txHash}`, 'GET', null, token);
    },
    
    // Add address to whitelist
    addToWhitelist: async (address, label = '') => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/whitelist', 'POST', {
            address,
            label
        }, token);
    },
    
    // Get whitelisted addresses
    getWhitelist: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/whitelist', 'GET', null, token);
    },
    
    // Remove address from whitelist
    removeFromWhitelist: async (id) => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/wallet/whitelist/${id}`, 'DELETE', null, token);
    },
    
    // Set default whitelist address
    setDefaultWhitelist: async (id) => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/wallet/whitelist/${id}/default`, 'PUT', null, token);
    },

    getUserDepositProofs: async (params = {}) => {
        const token = TokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/wallet/deposit-proof${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    submitDepositProof: async (data) => {
        const token = TokenService.getToken();
        return await apiRequest('/api/wallet/deposit-proof', 'POST', data, token);
    },
    
    uploadDepositScreenshot: async (file) => {
        const token = TokenService.getToken();
        const formData = new FormData();
        formData.append('screenshot', file);
        
        const url = `${API_BASE_URL}/api/wallet/upload-screenshot`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw {
                status: response.status,
                message: errorData.error || `HTTP error! status: ${response.status}`,
                data: errorData
            };
        }
        
        return await response.json();
    }
};

// KYC API
export const KYCAPI = {
    // Submit KYC verification
    submitKYC: async (formData) => {
        const token = TokenService.getToken();
        
        // For file uploads, we need to use FormData
        const url = `${API_BASE_URL}/api/kyc/submit`;
        const headers = {
            'Authorization': `Bearer ${token}`,
        };
        
        // Don't set Content-Type for FormData - browser will set it with boundary
        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw {
                status: response.status,
                message: errorData.error || `HTTP error! status: ${response.status}`,
                data: errorData
            };
        }
        
        return await response.json();
    },
    
    // Get KYC status
    getKYCStatus: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/kyc/status', 'GET', null, token);
    },
    
    // Upload KYC document
    uploadDocument: async (file, documentType) => {
        const token = TokenService.getToken();
        const formData = new FormData();
        formData.append('document', file);
        formData.append('type', documentType);
        
        const url = `${API_BASE_URL}/api/kyc/upload`;
        const headers = {
            'Authorization': `Bearer ${token}`,
        };
        
        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw {
                status: response.status,
                message: errorData.error || `HTTP error! status: ${response.status}`,
                data: errorData
            };
        }
        
        return await response.json();
    }
};

// Games API (Placeholder for future)
export const GamesAPI = {
    // Get available games
    getGames: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/games', 'GET', null, token);
    },
    
    // Place bet
    placeBet: async (gameId, amount, data = {}) => {
        const token = TokenService.getToken();
        return await apiRequest('/api/games/bet', 'POST', {
            game_id: gameId,
            amount: parseFloat(amount),
            ...data
        }, token);
    },

    // Get game history
    getGameHistory: async (params = {}) => {
        const token = TokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/games/history${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    }
};

// Stats API
export const StatsAPI = {
    // Get user statistics
    getUserStats: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/stats/user', 'GET', null, token);
    },
    
    // Get leaderboard
    getLeaderboard: async (period = 'daily') => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/stats/leaderboard?period=${period}`, 'GET', null, token);
    }
};

// Referral API
export const ReferralAPI = {
    // Get referral info
    getReferralInfo: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/referral/info', 'GET', null, token);
    },
    
    // Get referral stats
    getReferralStats: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/referral/stats', 'GET', null, token);
    },
    
    // Get referral history
    getReferralHistory: async (params = {}) => {
        const token = TokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/referral/history${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    }
};

// Support API
export const SupportAPI = {
    // Submit support ticket
    submitTicket: async (subject, message, category = 'general') => {
        const token = TokenService.getToken();
        return await apiRequest('/api/support/ticket', 'POST', {
            subject,
            message,
            category
        }, token);
    },
    
    // Get tickets
    getTickets: async (params = {}) => {
        const token = TokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/support/tickets${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get ticket by ID
    getTicket: async (id) => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/support/ticket/${id}`, 'GET', null, token);
    },
    
    // Reply to ticket
    replyToTicket: async (id, message) => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/support/ticket/${id}/reply`, 'POST', {
            message
        }, token);
    }
};

// Utility functions
export const APIUtils = {
    // Format Bitcoin amount
    formatBTC: (amount) => {
        if (amount === null || amount === undefined) return '0.00000000';
        return parseFloat(amount).toFixed(8);
    },
    
    // Format currency
    formatCurrency: (amount, currency = 'BTC') => {
        if (currency === 'BTC') {
            return `${parseFloat(amount).toFixed(8)} ${currency}`;
        }
        return `${parseFloat(amount).toFixed(2)} ${currency}`;
    },
    
    // Format date
    formatDate: (dateString, format = 'short') => {
        const date = new Date(dateString);
        if (format === 'short') {
            return date.toLocaleDateString();
        } else if (format === 'long') {
            return date.toLocaleString();
        }
        return dateString;
    },
    
    // Handle API errors
    handleError: (error, showNotification = true) => {
        // console.error('API Error:', error);
        
        if (showNotification) {
            // You can integrate with your notification system here
            const message = error.message || 'An error occurred. Please try again.';
            
            // Example using toast notifications
            if (typeof window !== 'undefined' && window.toast) {
                window.toast.error(message);
            } else {
                alert(message);
            }
        }
        
        // Handle specific error cases
        if (error.status === 401) {
            // Unauthorized - clear tokens and redirect to login
            TokenService.clear();
            window.location.href = '/login';
        }
        
        return error;
    }
};

// Bet API
export const BetAPI = {
    // Place a bet
    placeBet: async (betData) => {
        const token = TokenService.getToken();
        return await apiRequest('/api/bet/place', 'POST', betData, token);
    },

    placeAccumulatorBet: async (betData) => {
        const token = TokenService.getToken();
        return await apiRequest('/api/bet/place-accumulator', 'POST', betData, token);
    },
    
    // Get bet history
    getBetHistory: async (params = {}) => {
        const token = TokenService.getToken();
        const queryParams = new URLSearchParams(params).toString();
        const endpoint = `/api/bet/history${queryParams ? `?${queryParams}` : ''}`;
        return await apiRequest(endpoint, 'GET', null, token);
    },
    
    // Get specific bet details
    getBet: async (betId) => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/bet/${betId}`, 'GET', null, token);
    },
    
    // Cancel a pending bet
    cancelBet: async (betId) => {
        const token = TokenService.getToken();
        return await apiRequest(`/api/bet/cancel/${betId}`, 'POST', null, token);
    },
    
    // Get bet statistics
    getBetStatistics: async () => {
        const token = TokenService.getToken();
        return await apiRequest('/api/bet/statistics', 'GET', null, token);
    }
};


// Export the apiRequest function
export { apiRequest };

// Export all APIs
export default {
    AuthAPI,
    UserAPI,
    WalletAPI,
    KYCAPI,
    GamesAPI,
    StatsAPI,
    ReferralAPI,
    SupportAPI,
    APIUtils,
    TokenService,
    BetAPI,
    apiRequest
};